import React, { useEffect, useState } from "react";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import Icon from "../../Elements/Icon/Icon.tsx";
import AnimationContainer from "../../Reusable/AnimationContainer/AnimationContainer.js";
import useMediaQueries from "../../../hooks/useMediaQueries.js";
import Notifications from "../../Reusable/Notifications/Notifications.js";
import Image from "../../Elements/Image/Image.tsx";
import Button from "../../Elements/Button/Button.js";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import Cookies from "js-cookie";
import { LinkToNewTab } from "../../Elements/Link/Link.js";
import { useUser } from "../../../contexts/userContext/userContext.js";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import SidePanelButton from "components/Reusable/SidePanelButton/index.tsx";
import useOrganization from "../../../hooks/useOrganization.ts";
import checkCurrDomainIsOrgDomain from "helpers/checkCurrDomainIsOrgDomain.ts";
import { useParams } from "react-router-dom";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import { useProfile } from "contexts/profileContext/profileContext.js";

const isCustomDomain = checkCurrDomainIsOrgDomain();

const enumOptions = {
  profile: {
    icon: "profile",
    img: "home/Logo",
    title: "profile-home-info-title",
    text: "profile-home-info-text",
    btn: {
      title: "profile-home-info-button",
      link: "user/profile/details",
    },
  },
  branding: {
    icon: "branding",
    img: "home/Branding",
    title: "branding-home-info-title",
    text: "branding-home-info-text",
    btn: {
      title: "branding-home-info-button",
      link: "user/branding/cover-logo",
    },
  },
  signature: {
    icon: "sign",
    img: "home/Signature",
    title: "sign-home-info-title",
    text: "sign-home-info-text",
    btn: {
      title: "sign-home-info-button",
      link: "user/branding/email",
    },
  },
  jpay: {
    icon: "jpay",
    img: "home/Jumbopay",
    title: "jpay-home-info-title",
    text: "jpay-home-info-text",
    btn: {
      title: "jpay-home-info-button",
      link: "jumbo-pay",
    },
  },
  jpro: {
    icon: "jpro",
    img: "home/Pro",
    title: "jpro-home-info-title",
    text: "jpro-home-info-text",
    btn: {
      title: "jpro-home-info-button",
      link: "subscribers",
    },
  },
};

const InfoHomeComponent = ({ img, title, text, btn, state, orgDomain }) => {
  const { getTextByKey } = useContent();
  const { isSubscribed, getUserData } = useUser();
  const userData = getUserData();
  const isSubUser = getUserData().IsSubUser;

  const infoTitle =
    isSubscribed() && !isSubUser
      ? `${getTextByKey("your-subscription-plan-text")}: ${
          userData.Subscription
        }`
      : isSubUser
      ? `${getTextByKey("account-managed-under-organization-text")}`
      : getTextByKey(title);

  const btnLink =
    isSubscribed() || isSubUser
      ? "user/profile/account-settings"
      : `${btn.link}`;

  const btnTitle = isSubscribed() || isSubUser ? "profile" : `${btn.title}`;

  const mode = "out-in";
  return (
    <SwitchTransition mode={mode}>
      <CSSTransition
        key={state}
        addEndListener={(node, done) => {
          node.addEventListener("transitionend", done, false);
        }}
        classNames="fade"
      >
        <div>
          <Image name={img} ext="png" />
          <div className="info-title">{infoTitle}</div>
          {isSubUser && orgDomain && (
            <div className="info-title-org">{orgDomain}</div>
          )}
          {!isSubscribed() && !isSubUser && (
            <div className="info-text">{getTextByKey(text)}</div>
          )}
          <div
            className="btn-wrap"
            style={{
              marginTop: `${isSubscribed() || isSubUser ? "30px" : ""}`,
            }}
          >
            <Button>
              <LinkToNewTab page={btnLink} isNewTab={true}>
                {getTextByKey(btnTitle)}
              </LinkToNewTab>
            </Button>
            {isSubscribed() && !isSubUser && (
              <Button buttonClass="btn-link">
                <LinkToNewTab page={"user/profile/account"} isNewTab={true}>
                  {getTextByKey("upgrade-or-cancel-subscription-text")}
                </LinkToNewTab>
              </Button>
            )}
          </div>
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

export const HomeSidePanel = () => {
  const [openOptions, setOpenOptions] = useState(!isCustomDomain);
  const [openInfo, setOpenInfo] = useState(false);
  const { getUserData } = useUser();
  const isSubUser = getUserData().IsSubUser;

  const [state, setState] = useState(false);
  const { getDirection } = useContent();
  const [orgDomain, setOrgDomain] = useState(null);

  const { getIsIsrael } = useUser();
  const { desktopCondition } = useMediaQueries();
  const {
    getAdsBackground,
    getBlackFridayTime,
    getBlackFridayTimeCookie,
    getArtist,
  } = useContent();

  const { checkCurrDomainIsOrgDomain } = useOrganization();

  const handleClick = () => {
    setOpenInfo(false);
    setOpenOptions((prev) => !prev);
    if (openOptions) {
      // cookie to remember that side panel on this device is closed for 24 hours
      Cookies.set("sidePanelClosed", "true", { expires: 1 });
    }
  };

  const getOrganizationDomain = async () => {
    const response = await apiRequest("Users", "GetOrganizationDomain");
    return response.data;
  };

  // Make the request only if the user is a sub-user
  useEffect(() => {
    const fetchOrgDomain = async () => {
      if (isSubUser) {
        const response = await getOrganizationDomain();
        setOrgDomain(response.OrganizationDomain);
      }
    };

    fetchOrgDomain();
  }, [isSubUser]);

  useEffect(() => {
    const header = document.querySelector(".header");
    const subscriptionExpirationReminderCloseBtnEl = document.getElementById(
      "reminder-close-icon"
    );

    if (header) {
      if (openOptions && desktopCondition) {
        header.classList.add("home-side-panel-open");
      } else {
        header.classList.remove("home-side-panel-open");
      }
    }

    if (subscriptionExpirationReminderCloseBtnEl) {
      if (openOptions && desktopCondition) {
        subscriptionExpirationReminderCloseBtnEl.classList.add(
          "expiration-reminder-close"
        );
      } else {
        subscriptionExpirationReminderCloseBtnEl.classList.remove(
          "expiration-reminder-close"
        );
      }
    }
  }, [openOptions, desktopCondition, getDirection()]);

  useEffect(() => {
    // conditions not to show side panel info, campaing active and header is not closed, artist page or side panel closed by user
    if (
      getAdsBackground().adId ||
      (getBlackFridayTime() && getBlackFridayTimeCookie()) ||
      getArtist() ||
      Cookies.get("sidePanelClosed")
    ) {
      setOpenInfo(false);
      setOpenOptions(false);
    }
  }, [getBlackFridayTime(), getArtist(), getAdsBackground()]);

  return desktopCondition && !checkCurrDomainIsOrgDomain() ? (
    <div className={`side-panel${openOptions ? " open" : ""}`}>
      <SidePanelButton onClick={handleClick} />
      <div className="side-panel-options">
        <div className="options-list">
          {Object.entries(enumOptions).map(([key, val]) => {
            if (!getIsIsrael() && key === "jpay") return null;
            return (
              <div
                className="option-icon-container"
                key={key}
                onClick={() => {
                  setOpenInfo(key);
                  setState((prev) => !prev);
                }}
              >
                <Tooltip tooltipTextKey={val.title}>
                  <Icon name={val.icon} />
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>
      <AnimationContainer
        animationOn={!!openInfo}
        animationClassName="side-bar-animation"
      >
        <div className="options-info-container">
          <div
            className="close-info-container"
            onClick={() => setOpenInfo(false)}
          >
            <Icon name="x" gray />
          </div>
          {!openInfo ? null : openInfo === "noti" ? (
            <Notifications openNotifications={true} />
          ) : (
            <div className="option-container">
              <InfoHomeComponent
                {...enumOptions[openInfo]}
                state={state}
                orgDomain={orgDomain}
              />
            </div>
          )}
        </div>
      </AnimationContainer>
    </div>
  ) : null;
};
